import { action, computed, makeObservable, observable } from 'mobx'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowCategory from '~/client/src/shared/models/WorkflowCategory'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore, {
  IWorkflowTypeRuleChanges,
} from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { NOOP } from '~/client/src/shared/utils/noop'
import { toast } from '~/client/src/shared/utils/toast'

import PreviewPermitFormStore from './PreviewPermitForm.store'

export default class PermitsSetUpStore {
  @observable private activePermitTypeId: string = null
  @observable public isNewFormTypeDialogShown: boolean = false
  @observable public draftPermitType: WorkflowType = null
  @observable public isFieldMappingPhase: boolean = false

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly workflowCategoriesStore: WorkflowCategoriesStore,
    private readonly previewPermitStore: PreviewPermitFormStore,
  ) {
    makeObservable(this)
  }

  public get isReceived(): boolean {
    return (
      this.workflowTypesStore.isDataReceived &&
      this.workflowCategoriesStore.isDataReceived
    )
  }

  public get isUpdating(): boolean {
    const { loading } = this.appState
    return (
      loading.get(e.SAVE_WORKFLOW_TYPES) ||
      loading.get(e.REMOVE_WORKFLOW_TYPES) ||
      loading.get(e.SAVE_MANY_OPERATION_RULES) ||
      loading.get(e.DELETE_MANY_OPERATION_RULES)
    )
  }

  public get permitTypes(): WorkflowType[] {
    return this.workflowTypesStore.actualTypes
  }

  public get activeCategories(): WorkflowCategory[] {
    return this.workflowCategoriesStore.activeCategories
  }

  private get appState(): DesktopInitialState {
    return this.eventsStore.appState
  }

  @computed
  public get activePermitType(): WorkflowType {
    return (
      this.draftPermitType ||
      this.workflowTypesStore.getPermitTypeById(this.activePermitTypeId)
    )
  }

  public get isDraft(): boolean {
    return !!this.draftPermitType
  }

  @action.bound
  public showNewFormTypeDialog() {
    this.isNewFormTypeDialogShown = true
  }

  @action.bound
  public hideNewFormTypeDialog() {
    this.isNewFormTypeDialogShown = false
  }

  public createCustomPermitType = (
    name: string,
    categoryId: string,
    basedOnType: WorkflowType,
  ) => {
    if (!name?.trim() || !categoryId || !basedOnType) {
      return
    }

    this.hideNewFormTypeDialog()

    this.workflowTypesStore.createCustomType(name, categoryId, basedOnType)
  }

  public removeCustomPermitTypes = (typeIds: string[]) => {
    this.workflowTypesStore.deleteTypes(typeIds)
  }

  public saveFormType = (
    updatedFormType: WorkflowType,
    onError?: () => void,
    onSaved?: () => void,
    ruleChanges?: IWorkflowTypeRuleChanges,
  ) => {
    if (this.draftPermitType) {
      this.workflowTypesStore.saveMany(
        [updatedFormType],
        (savedTypes: WorkflowType[]) => {
          onSaved?.()
          this.clearDraft()
          this.showSuccessMsg(savedTypes)
        },
        onError,
        ruleChanges,
      )
    } else {
      this.changePermitType(
        this.activePermitType,
        updatedFormType,
        (savedTypes: WorkflowType[]) => {
          onSaved?.()
          this.showSuccessMsg(savedTypes)
        },
        onError,
        ruleChanges,
      )
    }
  }

  @action.bound
  public changeShowInApp(fTypeId: string) {
    const updatingPermitType =
      this.workflowTypesStore.getPermitTypeById(fTypeId)
    this.changePermitType(updatingPermitType, {
      isEnabled: !updatingPermitType.isEnabled,
    })
  }

  @action.bound
  public setActivePermitTypeId(
    activePermitTypeId: string = this.permitTypes[0]?.id,
  ) {
    this.activePermitTypeId = activePermitTypeId

    this.previewPermitStore.setDefaultSitePermit(null, activePermitTypeId, true)
    this.previewPermitStore.setPreviewTemplate(this.activePermitType)
  }

  @action.bound
  public setDraftPermitType(permitType: WorkflowType) {
    this.activePermitTypeId = null
    this.draftPermitType = permitType
    this.isFieldMappingPhase = true
    this.previewPermitStore.setDefaultSitePermit(null, null, true)
    this.previewPermitStore.setPreviewTemplate(permitType)
  }

  @action.bound
  public completeFieldMapping() {
    this.isFieldMappingPhase = false
    this.previewPermitStore.setPreviewTemplate(this.draftPermitType)
  }

  @action.bound
  public resetSelectedFormType() {
    this.activePermitTypeId = null
    this.draftPermitType = null
    this.previewPermitStore.setPreviewTemplate(null)
  }

  @action.bound
  public showSuccessMsg([{ id }]: WorkflowType[]) {
    this.setActivePermitTypeId(id)
    toast.success(Localization.translator.successfullyUpdated)
  }

  public resetSelectedFormIfNeed = () => {
    if (this.activePermitType?.isDeleted) {
      this.resetSelectedFormType()
    }
  }

  @action.bound
  private clearDraft() {
    this.draftPermitType = null
  }

  @action.bound
  private changePermitType(
    updatingPermitType: WorkflowType,
    updatedFields: any,
    postAction: (permitTypes: WorkflowType[]) => void = NOOP,
    onError?: () => void,
    ruleChanges?: IWorkflowTypeRuleChanges,
  ) {
    const permitType: WorkflowType = Object.assign(
      updatingPermitType.getCopy(),
      updatedFields,
    )

    this.workflowTypesStore.saveMany(
      [permitType],
      postAction,
      onError,
      ruleChanges,
    )
  }
}
